import { useQuery } from "react-query";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { CpcArraySchema, type Cpc, cpcSelectorApiKey } from "./useCpcSelector";

export const CPC_MAX_LEVEL = 5;
export const CPC_ROOT_LEVEL = 1;

export const canDrillDownCpc = (cpc: Cpc): boolean => (cpc.level ?? CPC_MAX_LEVEL) < CPC_MAX_LEVEL;

type UseCpcListOptions = {
    parentCode?: string | null;
    searchText?: string;
    enabled?: boolean;
    itemsPerPage?: number;
};

export const useCpcList = (options: UseCpcListOptions = {}) => {
    const { parentCode = null, searchText = "", enabled = true, itemsPerPage = 100 } = options;
    const trimmedSearch = searchText.trim();
    const isSearchMode = trimmedSearch.length > 0;
    const isRootLevel = parentCode === null || parentCode === "";

    const query = useQuery({
        queryKey: [cpcSelectorApiKey, "list", parentCode ?? "root", trimmedSearch, itemsPerPage],
        queryFn: async () => {
            const params: Record<string, string | number | Record<string, string>> = {
                itemsPerPage,
                order: { code: "ASC" },
            };

            if (isSearchMode) {
                params.title = trimmedSearch;
            } else if (!isRootLevel) {
                params.parent = parentCode;
            } else {
                params.level = CPC_ROOT_LEVEL;
            }

            const response = await loggedInApiCall.get<{ "hydra:member": unknown; "hydra:totalItems": number }>(
                "/api/cpc",
                { params },
            );

            return response.data;
        },
        select: (data) => {
            if (!data?.["hydra:member"]) {
                return { cpcItems: [], totalItems: 0 };
            }

            const cpcItems = zodParseWithLog(data["hydra:member"], CpcArraySchema, `${cpcSelectorApiKey}-list`);

            return {
                cpcItems,
                totalItems: data["hydra:totalItems"] ?? cpcItems.length,
            };
        },
        enabled,
        staleTime: 60_000,
        cacheTime: 600_000,
    });

    return {
        cpcItems: query.data?.cpcItems ?? [],
        totalItems: query.data?.totalItems ?? 0,
        isSearchMode,
        ...query,
    };
};
